import {ScreenComponent} from './ScreenComponent.js';
import {
  div,
  el,
  getStyleSheet,
  removeAllChildren, setCSSRule
} from '@megaphonenpm/mputils/src/DOM.js';
import {CHANGE, COMPLETE} from '@megaphonenpm/mputils/src/constants.js';
import {get} from 'lodash-es';
import {STATE_SCENE, STATE_SCENE_RESULT} from '@megaphonenpm/mrtsclient/src/constants.js';
import {MegaScreen} from '../MegaScreen.js';
import {
  cascadingGet,
  formatElapsed,
  getIfNotEmpty,
  notEmpty,
  notNil
} from '@megaphonenpm/mputils/src/utils.js';
import moment from 'moment/src/moment.js';
import {MRTSConnectionEvent} from '@megaphonenpm/mrtsclient/src/MRTSConnectionEvent.js';
import {play} from '../Animation.js';

/**
 * Displays a list of topics with a countdown, highlighting the current topic. Topics are scenes
 * in a playlist.
 */
export class RundownComponent extends ScreenComponent {

  constructor () {
    super();
    this.currentSceneID = null;
    this.countdownEndedTimeout = -1;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === 'visible') {
        this.syncCurrentScene();
        this.updateTimeRemaining();
      }
    });
  }

  //===============================================================================================
  // BUILD
  //===============================================================================================

  build () {
    super.build();
    this.view = div(null, 'rundown');
    this.sceneslist = el('ul', null, 'sceneslist');
    this.sceneCountdown = div( null, 'sceneCountdown');
    this.playlistTitle = div( null, 'title');
    this.bottomShadow = div(null, 'bottomShadow');  // Overlay that fades out bottom scene
    this.view.append(
      this.sceneCountdown,
      this.playlistTitle,
      this.sceneslist,
      this.bottomShadow);
  }

  //===============================================================================================
  // BOOTMANAGER DEPENDENCY
  //===============================================================================================
  setBootManager (bootManager) {
    this.bootManager = bootManager;
    this.registerBootManagerListeners();
  }

  registerBootManagerListeners () {
    this.bootManager.on(COMPLETE, this.bootCompleteListener.bind(this));
  }

  bootCompleteListener (e) {
    // Force an update because the first syncCurrentScene() call will happen in response to an
    // appState update before the current playlist has been rendered in the rundown.
    this.update();
  }

  //===============================================================================================
  // MRTSCONNECTION DEPENDENCY
  //===============================================================================================
  setMRTSConnection (mrtsConnection) {
    this.mrtsConnection = mrtsConnection;
    this.registerMRTSConnectionListeners();
  }

  registerMRTSConnectionListeners () {
    this.mrtsConnection.on(MRTSConnectionEvent.CONTENT_READY, this.mrtsContentReadyListener.bind(this));
  }

  /**
   * Triggered whenever MegaScreen receives a new playlist from MRTS (e.g., due to Update Playlist).
   */
  mrtsContentReadyListener (e) {
    if (this.bootManager.complete) {
      this.checkEnabled();

      this.currentSceneID = null;
      this.view.classList.remove('countdownEnded');
      this.updatePlaylist();
      this.syncCurrentScene();
      this.updateTimeRemaining();
    }
  }

  //===============================================================================================
  // APPSTATE DEPENDENCY
  //===============================================================================================
  setAppState (appState) {
    this.appState = appState;
    this.registerForAppStateChanges();
  }

  registerForAppStateChanges () {
    this.appState.on(CHANGE, e => {
      this.syncToState(e.detail);
    });
  }

  syncToState (changedProp) {
    let prop = get(changedProp, 'prop');
    if (prop === 'state') {
      this.view.classList.remove('visible_state');
      if (this.appState.state === STATE_SCENE
          || this.appState.state === STATE_SCENE_RESULT) {
        this.view.classList.add('visible_state');
        this.syncCurrentScene();
      }
    } else if (prop === 'timeRemaining') {
      this.updateTimeRemaining();
    }
  }

  syncCurrentScene () {
    if (document.hidden || !this.enabled) {
      return;
    }

    let currentSceneID = get(MegaScreen.globals.appState, 'scene.id');
    if (notEmpty(currentSceneID) && this.currentSceneID !== currentSceneID) {
      // Apply new distance to slide off for rundown width specified by current theme
      this.updateCurrentScene();
    }
  }

  //===============================================================================================
  // DISPLAY MANAGEMENT
  //===============================================================================================
  /**
   * Updates all aspects of the view.
   */
  update () {
    this.checkEnabled();
    this.updatePlaylist();
    this.updateCurrentScene();
    this.updateTimeRemaining();
  }

  /**
   * Updates the list of scenes in the rundown.
   */
  updatePlaylist () {
    this.playlistTitle.textContent = getIfNotEmpty(MegaScreen.globals.dataStore,
      'playlist.name', 'Rundown');
    this.updateTimeRemaining();

    // Recreate the list of scenes every time the playlist changes.
    removeAllChildren(this.sceneslist);
    let scenes = get(MegaScreen.globals.dataStore, 'playlist.scenes', []);
    scenes.forEach(scene => {
      // To avoid errors accessing the element by id with querySelector(), add a leading underscore to the id
      let li = el('li', `_${scene.id}`);
      // For the scene's text, use the question text (surveys/quizzes), or the title (bulletins),
      // or the social message (Tweet to TV, etc). In future, consider updating the scene object
      // model to include "rundown titles" for every scene type, which would allow a survey question
      // to appear differently in a rundown than in the lower third.
      li.innerHTML = cascadingGet(scene, ['content.question.text',
          'content.title.text',
          'content.social.message.text'], '--');

      this.sceneslist.append(li);
    });
  }

  /**
   * Updates the rundown to visually reflect the current scene.
   */
  updateCurrentScene () {
    let sceneListItems = this.sceneslist.querySelectorAll('li');
    let currentSceneIndex = MegaScreen.globals.playlistService.getSceneIndex(MegaScreen.globals.appState.scene);

    sceneListItems.forEach((sceneListItem, index) => {
      // Clear all prior class assignments
      sceneListItem.classList.remove(...sceneListItem.classList);

      // For any scene before the currently active scene, add the CSS class 'past'.
      if (notNil(currentSceneIndex)) {
        if (currentSceneIndex > index) {
          sceneListItem.classList.add('past');
        } else if (currentSceneIndex === index) {
          sceneListItem.classList.add('current');
          // Play a "scale in" animation on the new current scene
          play(sceneListItem, 'flash-scale-in');
        } else {
          // Play an "animate up" animation on all scenes after the current scene
          play(sceneListItem, 'slide-in-up-short');
        }
      }
    });

    // When all scenes would fit within the rundown without any vertical overflow, reduce the opacity
    // of the bottom shadow that fades out the bottom scene. This provides a graceful visual transition
    // for content that overflows without an overly dark shadow at the bottom of the rundown when
    // content does not overflow.
    if (this.sceneslist.offsetHeight + this.playlistTitle.offsetHeight + this.sceneCountdown.offsetHeight < this.view.offsetHeight) {
      this.bottomShadow.style.opacity = .4;
    } else {
      this.bottomShadow.style.opacity = 1;
    }

    this.currentSceneID = get(MegaScreen.globals.appState, 'scene.id');
  }

  /**
   * Updates the on-screen countdown for the current scene.
   */
  updateTimeRemaining () {
    if (document.hidden || !this.enabled) {
      return;
    }
    let timeRemaining = parseInt(MegaScreen.globals.appState.timeRemaining);
    let timeRemainingOutputString;

    let currentSceneID = get(MegaScreen.globals.appState.scene, 'id')
    let currentSceneListItem = this.sceneslist.querySelector(`li#_${currentSceneID}`);
    if (!isNaN(timeRemaining) && timeRemaining <= 1) {
      if (!this.view.classList.contains('countdownEnded')) {
        clearTimeout(this.countdownEndedTimeout);
        setTimeout(() => {
          this.view.classList.add('countdownEnded');
        }, 200);
        /* When the countdown ends, animate the current scene off the screen */
        play(currentSceneListItem, 'slide-off-right', 300);
      }
    } else {
      this.view.classList.remove('countdownEnded');
      if (notNil(currentSceneListItem)) {
        // Workaround for a very rare MRTS bug: if Update Playlist is sent to MRTS exactly at the
        // end of the scene, MRTS will occasionally restart the scene. If that happens, the current
        // scene will have animated off screen already, and will appear missing from the rundown. To
        // handle that case, remove the animation class from the <li> so it appears normally in the
        // rundown.
        currentSceneListItem.classList.remove('slide-off-right');
      }
    }

    if (isNaN(timeRemaining) || timeRemaining === 0) {
      // If the countdown is over or not running, add an empty space to the sceneCountdown <div>,
      // which forces it to occupy vertical space. Without any content, the <div> would collapse.
      // Do this instead of hardcoding the <div> height so the <div> automatically adjust its height
      // to accommodate the specified font size.
      timeRemainingOutputString = '&nbsp;';
    } else {
      let now = moment();
      // Calculate the time when the countdown will end (moment(now) clones the now moment)
      let countdownEndTime = moment(now).add(timeRemaining, 'seconds');
      timeRemainingOutputString = formatElapsed(moment.preciseDiff(now, countdownEndTime, true),
        'minutes',
        false,
        false);
    }
    this.sceneCountdown.innerHTML = timeRemainingOutputString;
  }

  // TODO: Consider moving enable()/disable() to Component
  enable () {
    setCSSRule(getStyleSheet('main'), '.rundown', 'display: block');
    this.enabled = true;
  }

  disable () {
    setCSSRule(getStyleSheet('main'), '.rundown', 'display: none');
    this.enabled = false;
  }

  checkEnabled () {
    if (get(MegaScreen.globals.dataStore.deploymentSettings, 'screen.rundown_enabled') === true) {
      this.enable();
    } else {
      this.disable();
    }
  }

  getCSS () {
    let css = `
    .applicationRoot .rundown {
      position: absolute;
      overflow: hidden;
      /* Magic code required to fix "disappearing elements" bug in Chrome, where scale interacts
         poorly with overflow:hidden. See:
         https://stackoverflow.com/questions/33617317/div-becomes-invisble-but-remains-clickable-when-scaling-content-only-in-chrome
         */
      transform: translate3d( 0,0,0 );
    }
    
    .rundown .sceneCountdown {
      /* Allows use of z-index */
      position: relative;
      /* Move element to the top so it can have a drop shadow that appears on top of siblings */
      z-index: 1;
      /* Animate the fade-in and fade-out of the text when the countdown ends */
      transition: color 500ms ease-out;
    }
    
    .rundown.countdownEnded .sceneCountdown {
      animation: flash 500ms cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
      color: rgba(0, 0, 0, 0);
    }

    @keyframes flash {
      0% {
        background: #ffffff;
      }
    }
    
    .flash-scale-in {
      animation: flashScaleIn 300ms cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    }
        
    @keyframes flashScaleIn {
      0% {
        background: #ffffff;
        transform: scaleY(2) scaleX(2) translate(0px, 0px);
        transform-origin: 0% 0%;
      }
      100% {
        transform: scaleY(1) scaleX(1) translate(0px, 0px);
        transform-origin: 0% 0%;
      }
    }
    
    .slide-off-right {
      animation: slideOffRight 400ms cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    }
    
    /* Fade out, then slide to the right */
    @keyframes slideOffRight {
      0% {
        transform: translate(0px, 0px);
        transform-origin: 0% 0%;
      }
      80% {
        transform: translate(0px, 0px);
        transform-origin: 0% 0%;
        opacity: .5;
      }
      100% {
        transform: translate(125%, 0px);
        transform-origin: 0% 0%;
      }
    }
    
    .rundown .sceneslist {
      list-style-type: none;
      flex: 1 0 0;
      /* TODO: Calculate dynamically */
      min-height: 150px;
    }    
    
    /* Overlay at the bottom of the rundown that fades out the bottom scene when it is partly cut off.
       Note that when all scenes would fit within the rundown without any vertical overflow, the opacity 
       of the shadow is dynamically reduced within updateCurrentScene(). 
     */
    .rundown .bottomShadow {
      position: absolute;
      bottom: 0;
      height: 100%;
      width: 100%;
      box-shadow: inset 0 -30px 35px -30px rgba(0,0,0,.85);
      transition: opacity 250ms ease-out;
      pointer-events: none;
    }
    
    /* Hide scenes before the current scene in the playlist */
    .rundown .sceneslist li.past {
      display: none;
    }
    
    
    .rundown .sceneslist li {
      position: relative;
    }
    `;

    return css;
  }
}
